import { AlertTriangle, Trash2, X } from "lucide-react";
import {
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export interface ArchiveAccountDialogProps {
  entityKind: "business" | "user";
  entityLabel: string;
  confirmation: string;
  busy: boolean;
  error?: string | undefined;
  impact: ReactNode[];
  onCancel: () => void;
  onConfirm: (confirmation: string) => void;
}

export function ArchiveAccountDialog({
  entityKind,
  entityLabel,
  confirmation,
  busy,
  error,
  impact,
  onCancel,
  onConfirm,
}: ArchiveAccountDialogProps) {
  const [typedConfirmation, setTypedConfirmation] = useState("");
  const titleId = useId();
  const descriptionId = useId();
  const errorId = useId();
  const dialog = useRef<HTMLElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const matches = typedConfirmation === confirmation;
  const actionLabel =
    entityKind === "business" ? "Delete & archive business" : "Delete & archive user";

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => {
      input.current?.focus();
    });
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== "Tab" || !dialog.current) return;
      const controls = Array.from(
        dialog.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = controls[0];
      const last = controls.at(-1);
      if (!first || !last) {
        event.preventDefault();
        dialog.current.focus();
      } else if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === dialog.current)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
      previousFocus?.focus();
    };
  }, [busy, onCancel]);

  return createPortal(
    <div className="archive-account-layer" role="presentation">
      <div className="archive-account-layer__scrim" aria-hidden="true" />
      <section
        className="archive-account-dialog"
        ref={dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
      >
        <button
          className="archive-account-dialog__close"
          type="button"
          aria-label="Cancel deletion"
          disabled={busy}
          onClick={onCancel}
        >
          <X />
        </button>
        <span className="archive-account-dialog__icon" aria-hidden="true">
          <AlertTriangle />
        </span>
        <p className="eyebrow">Permanent account action</p>
        <h2 id={titleId}>
          Delete and archive {entityLabel}?
        </h2>
        <p id={descriptionId} className="archive-account-dialog__intro">
          This action removes the account from the live platform and cannot be undone.
        </p>
        <ul className="archive-account-dialog__impact">
          {impact.map((item, index) => (
            <li key={index}>
              <Trash2 aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <label className="archive-account-dialog__confirmation">
          <span>
            Type <strong>{confirmation}</strong> to confirm
          </span>
          <input
            ref={input}
            value={typedConfirmation}
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            onChange={(event) => {
              setTypedConfirmation(event.target.value);
            }}
          />
        </label>
        {error && (
          <p className="archive-account-dialog__error" id={errorId} role="alert">
            {error}
          </p>
        )}
        <div className="archive-account-dialog__actions">
          <button className="button button--outline" type="button" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          <button
            className="button archive-account-dialog__danger"
            type="button"
            disabled={!matches || busy}
            onClick={() => {
              onConfirm(typedConfirmation);
            }}
          >
            <Trash2 /> {busy ? "Archiving…" : actionLabel}
          </button>
        </div>
      </section>
    </div>,
    document.getElementById("root") ?? document.body,
  );
}
